import { Paper, Stack, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faBuilding,
	faEnvelope,
	faNoteSticky,
	faPhone,
	faTag,
	faUser,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

/** The contact block as it came back on a sale or offer payload. */
export interface ContactDetails {
	label?: string | null;
	con_name?: string | null;
	con_phone?: string | null;
	con_email?: string | null;
	con_address?: string | null;
	con_details?: string | null;
	client_addr?: string | null;
}

const Row = ({ icon, label, value }: { icon: any; label: string; value: string }) => (
	<Stack direction="row" spacing={1.5} alignItems="flex-start">
		<FontAwesomeIcon icon={icon} style={{ width: 14, color: "#64748b", marginTop: 4 }} />
		<Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>{label}:</Typography>
		<Typography variant="body2" fontWeight={600} sx={{ whiteSpace: "pre-line" }}>{value}</Typography>
	</Stack>
);

export const hasContactDetails = (details?: ContactDetails | null): boolean =>
	Boolean(
		details
		&& [details.con_name, details.con_phone, details.con_email, details.con_address, details.con_details, details.label]
			.some((value) => String(value ?? "").trim()),
	);

/**
 * The address line as printed on the document, shown beside the document's own
 * details in the report modals.
 */
const ContactDetailsPanel = ({ details }: { details?: ContactDetails | null }) => {
	const { t } = useTranslation();
	if (!hasContactDetails(details)) return null;

	const rows: Array<[any, string, string]> = [
		[faTag, t("common:addressLine.label"), String(details?.label ?? "").trim()],
		[faUser, t("common:addressLine.name"), String(details?.con_name ?? "").trim()],
		[faPhone, t("common:addressLine.phone"), String(details?.con_phone ?? "").trim()],
		[faEnvelope, t("common:addressLine.email"), String(details?.con_email ?? "").trim()],
		[faBuilding, t("common:addressLine.address"), String(details?.con_address ?? "").trim()],
		[faNoteSticky, t("common:addressLine.details"), String(details?.con_details ?? "").trim()],
	];

	return (
		<>
			<Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 800 }}>
				{t("common:addressLine.title")}
			</Typography>
			<Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: "background.default", borderStyle: "dashed" }}>
				<Stack spacing={1.5}>
					{rows
						.filter(([, , value]) => value)
						.map(([icon, label, value]) => (
							<Row key={label} icon={icon} label={label} value={value} />
						))}
				</Stack>
			</Paper>
		</>
	);
};

export default ContactDetailsPanel;
